import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAllPlans, upgradeWorkspacePlan } from "../../client/api";
import { useUserContext } from "../../context/UserContext";
import Cookies from "js-cookie";
import useStyles from "./styles";
import { THEME_COLORS } from "../../context/constants";
import LightningArrowIcon from "../../icons/LightningArrowIcon";
import LogoSvg from "../../icons/Logo";
import CheckIcon from "../../icons/CheckIcon";

const PlanSelection: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { user, currentWorkspace, setCurrentWorkspace } = useUserContext();
  const [plans, setPlans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [upgradedPlanName, setUpgradedPlanName] = useState("");
  const customTextList = [
    "All features",
    "5 users/seats",
    "Contact us and customize your plan",
  ];

  useEffect(() => {
    // Check if user is logged in
    const authInfo = Cookies.get("authInfo");
    setIsLoggedIn(authInfo ? JSON.parse(authInfo).userId : null);
    const fetchPlans = async () => {
      try {
        const data = await getAllPlans();
        setPlans(data);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };
    fetchPlans();
  }, []);

  const handleUpgrade = async (planName: string) => {
    if (!user || !isLoggedIn) {
      navigate("/signup");
    }
    if (!user?.ownedWorkspaces?.[0]?._id) {
      console.error("No workspace found");
      return;
    }

    setIsLoading(true);
    try {
      const workspace = await upgradeWorkspacePlan(
        user.ownedWorkspaces[0]._id,
        planName
      );
      setCurrentWorkspace(workspace);
      setUpgradedPlanName(planName);
      setUpgradeSuccess(true);
    } catch (error) {
      console.error("Error upgrading plan:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCardStyle = (index: number) => {
    const isEven = index % 2 === 0;
    return {
      backgroundColor: isEven ? THEME_COLORS.lightYellow3 : THEME_COLORS.yellow,
    };
  };

  const getButtonStyle = (index: number) => {
    const isEven = index % 2 === 0;
    return {
      border: `2px solid ${isEven ? THEME_COLORS.yellow : THEME_COLORS.white}`,
      color: isEven ? THEME_COLORS.yellow : THEME_COLORS.white,
      "&:hover": {
        backgroundColor: isEven ? THEME_COLORS.lightYellow : THEME_COLORS.black,
      },
    };
  };

  const getPlanNameStyle = (index: number) => {
    const isEven = index % 2 === 0;
    return {
      backgroundColor: isEven ? THEME_COLORS.yellow : THEME_COLORS.white,
      color: isEven ? THEME_COLORS.lightYellow : THEME_COLORS.yellow,
    };
  };

  const renderPlanCard = (plan: any, index: number) => {
    if (!!plan.isCustom) return; //don't show plans added for specific workspaces, only the generic custom plan
    const isCustom = plan.name === "custom";

    return (
      <Grid item xs={12} md={4} key={plan._id}>
        <Card
          sx={{
            height: "100%",
            display: "flex",
            borderRadius: "40px",
            margin: "20px",
            flexDirection: "column",
            padding: "4px",
            ...getCardStyle(index),
          }}
        >
          <CardContent
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: "bold",
                borderRadius: "40px",
                width: "50%",
                mb: 4,
                ...getPlanNameStyle(index),
              }}
            >
              {plan.name.toUpperCase()}
            </Typography>

            {!isCustom ? (
              <>
                <Typography
                  variant="h4"
                  sx={{
                    color: THEME_COLORS.black,
                    mb: 2,
                  }}
                >
                  ${plan.pricePerUGC}/UGC
                </Typography>
              </>
            ) : (
              <Typography
                variant="h4"
                sx={{
                  color: THEME_COLORS.black,
                  mb: 2,
                }}
              >
                CUSTOM
              </Typography>
            )}

            <Button
              variant="outlined"
              sx={{
                mb: 3,
                fontWeight: 700,
                textTransform: "none",
                fontSize: "21px",
                width: "70%",
                borderRadius: "10px",
                ...getButtonStyle(index),
              }}
              onClick={() => handleUpgrade(plan.name)}
              disabled={isLoading || currentWorkspace?.plan?.name === plan.name}
            >
              Get started
            </Button>
            <Box className={classes.alignedLeft}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: THEME_COLORS.black,
                }}
              >
                {isCustom ? "SERVICES" : "INCLUDES"}
              </Typography>
            </Box>
            {!isCustom && (
              <>
                <Box className={classes.alignedLeft}>
                  <Typography variant="body1">
                    <CheckIcon
                      width={10}
                      height={8}
                      color={
                        index % 2 === 0
                          ? THEME_COLORS.yellow
                          : THEME_COLORS.white
                      }
                    />{" "}
                    {plan.videoLimit} videos
                  </Typography>
                  <Typography variant="body1">
                    <CheckIcon
                      width={10}
                      height={8}
                      color={
                        index % 2 === 0
                          ? THEME_COLORS.yellow
                          : THEME_COLORS.white
                      }
                    />{" "}
                    {plan.seats} users/teams
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ mt: 2, mb: 2 }}
                    align="left"
                  >
                    Everything an AI UGC Software that you need at your
                    fingertips...
                  </Typography>
                </Box>

                <List dense>
                  {plan.features.map((feature: string, idx: number) => (
                    <ListItem
                      key={idx}
                      sx={{ py: 0, display: "flex", alignItems: "center" }}
                    >
                      <Box
                        sx={{
                          mr: 1,
                          color:
                            index % 2 === 0
                              ? THEME_COLORS.white
                              : THEME_COLORS.yellow,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <CheckIcon
                          width={10}
                          height={8}
                          color={
                            index % 2 === 0
                              ? THEME_COLORS.yellow
                              : THEME_COLORS.white
                          }
                        />
                      </Box>
                      <ListItemText
                        primary={formatFeatureText(feature)}
                        primaryTypographyProps={{
                          variant: "body2",
                          color: THEME_COLORS.black,
                        }}
                        sx={{ marginTop: "2px" }} // Adjust alignment if needed
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {isCustom && (
              <List dense>
                {customTextList.map((feature: string, idx: number) => (
                  <ListItem
                    key={idx}
                    sx={{ py: 0, display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        mr: 1,
                        color:
                          index % 2 === 0
                            ? THEME_COLORS.white
                            : THEME_COLORS.yellow,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <CheckIcon
                        width={10}
                        height={8}
                        color={
                          index % 2 === 0
                            ? THEME_COLORS.yellow
                            : THEME_COLORS.white
                        }
                      />
                    </Box>
                    <ListItemText
                      primary={formatFeatureText(feature)}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: THEME_COLORS.black,
                      }}
                      sx={{ marginTop: "2px" }} // Adjust alignment if needed
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const formatFeatureText = (feature: string) => {
    const featureMap: Record<string, string> = {
      ADVANCED_AVATARS: "Access to Advanced Avatars",
      CUSTOM_VARIABLES: "Access to custom variables for messaging",
      MULTIPLE_GENERATION: "Generate multiple videos from one script",
      ELEVEN_LABS: "Eleven Labs integration",
      ELEVEN_LABS_OWN_KEY: "Use your own Eleven Labs API key",
      GPT_SCRIPT_GENERATION: "GPT-powered script writing",
      CSV_UPLOAD: "CSV upload for batch processing",
      VIDEO_DURATION_2: "Up to 2 min duration for videos",
    };
    return featureMap[feature] || feature;
  };

  return (
    <Box className={classes.backgroundContainer}>
      {/* Lightning arrow background */}
      <Box className={classes.lightningBg}>
        <LightningArrowIcon
          width={800}
          height={600}
          color={THEME_COLORS.yellow}
        />
      </Box>
      <Box className={classes.pageContainer}>
        <Box
          className={classes.buttonBox}
          sx={{ justifyContent: isLoggedIn ? "space-between" : "left" }}
        >
          <LogoSvg />
          {isLoggedIn && (
            <Button
              className={classes.backButton}
              onClick={() => navigate("/")}
            >
              {!!currentWorkspace?.plan ||
              currentWorkspace?.category === "fullaccess"
                ? "Back to application"
                : "Continue as a free user"}
            </Button>
          )}
        </Box>

        <Typography variant="h4" className={classes.title} gutterBottom>
          Pick a plan and Let's Launch!
        </Typography>
        <Typography variant="h6" className={classes.subtitle} gutterBottom>
          Step into the future of advertising with Let's Launch AI video
          generator and a suite of AI tools for marketers. Save big with annual
          billing. Cancel anytime.
        </Typography>

        <Grid container className={classes.plansContainer}>
          {plans.map(renderPlanCard)}
        </Grid>
      </Box>
      {upgradeSuccess && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              position: "relative",
              backgroundColor: THEME_COLORS.white,
              borderRadius: "40px",
              padding: "40px",
              maxWidth: "600px",
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            {/* Lightning background */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                opacity: 0.1,
                zIndex: -1,
              }}
            >
              <LightningArrowIcon
                width={400}
                height={300}
                color={THEME_COLORS.yellow}
              />
            </Box>

            {/* Logo */}
            <Box sx={{ mb: 3 }}>
              <LogoSvg width={120} height={60} />
            </Box>

            {/* Success message */}
            <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }}>
              {upgradedPlanName === "custom"
                ? "Custom Plan Request Received!"
                : `Upgrade Successful!`}
            </Typography>

            <Typography variant="body1" sx={{ mb: 3 }}>
              {upgradedPlanName === "custom"
                ? "For now you have all the PRO advantages. Our team will contact you soon to customize your perfect plan!"
                : `Enjoy your ${upgradedPlanName.toUpperCase()} plan advantages!`}
            </Typography>

            <Typography variant="body2" sx={{ mb: 4, fontStyle: "italic" }}>
              {upgradedPlanName === "custom"
                ? ""
                : "You'll receive an invoice when it's time to pay."}
            </Typography>

            <Button
              variant="contained"
              sx={{
                backgroundColor: THEME_COLORS.yellow,
                color: THEME_COLORS.black,
                fontWeight: "bold",
                borderRadius: "10px",
                padding: "10px 30px",
                "&:hover": {
                  backgroundColor: THEME_COLORS.darkYellow,
                },
              }}
              onClick={() => navigate("/")}
            >
              Let's Launch!
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PlanSelection;
